// @ts-check
/** @typedef {import('../types/api').ProductImage} ProductImage */
/** @typedef {import('../types/api').ProductVariant} ProductVariant */

import api from '../utils/apiClient';
import { assertArray } from '../utils/apiGuards';

const adminProductImageService = {
  /**
   * @param {string} productId
   * @returns {Promise<ProductImage[]>}
   */
  getProductImages: async (productId) => {
    const data = await api.get(`/api/admin/products/${productId}/images`);
    assertArray(data, 'adminProductImageService.getProductImages');
    return data;
  },

  /**
   * @param {string} productId
   * @param {Object} imageData
   * @returns {Promise<ProductImage>}
   */
  addProductImage: async (productId, imageData) => {
    return api.post(`/api/admin/products/${productId}/images`, imageData);
  },

  /**
   * @param {string} productId
   * @param {string} imageId
   * @param {Object} imageData
   * @returns {Promise<ProductImage>}
   */
  updateProductImage: async (productId, imageId, imageData) => {
    return api.put(`/api/admin/products/${productId}/images/${imageId}`, imageData);
  },

  /**
   * @param {string} productId
   * @param {string} imageId
   * @returns {Promise<void>}
   */
  deleteProductImage: async (productId, imageId) => {
    return api.del(`/api/admin/products/${productId}/images/${imageId}`);
  },
};

const adminProductVariantService = {
  /**
   * @param {string} productId
   * @returns {Promise<ProductVariant[]>}
   */
  getProductVariants: async (productId) => {
    const data = await api.get(`/api/admin/products/${productId}/variants`);
    assertArray(data, 'adminProductVariantService.getProductVariants');
    return data;
  },

  /**
   * @param {string} productId
   * @param {Object} variantData
   * @returns {Promise<ProductVariant>}
   */
  addProductVariant: async (productId, variantData) => {
    return api.post(`/api/admin/products/${productId}/variants`, variantData);
  },

  /**
   * @param {string} productId
   * @param {string} variantId
   * @param {Object} variantData
   * @returns {Promise<ProductVariant>}
   */
  updateProductVariant: async (productId, variantId, variantData) => {
    return api.put(`/api/admin/products/${productId}/variants/${variantId}`, variantData);
  },

  /**
   * @param {string} productId
   * @param {string} variantId
   * @returns {Promise<void>}
   */
  deleteProductVariant: async (productId, variantId) => {
    return api.del(`/api/admin/products/${productId}/variants/${variantId}`);
  },
};

const adminProductAiService = {
  /**
   * @param {Object} facts
   * @returns {Promise<{description: string, short_description: string}>}
   */
  generateDescription: async (facts) => {
    return api.post('/api/admin/products/generate-description', facts);
  },

  /**
   * @param {{image_url: string, name: string, product_type?: string}} facts
   * @returns {Promise<{alt_text: string}>}
   */
  generateAltText: async (facts) => {
    return api.post('/api/admin/products/generate-alt-text', facts);
  },

  /**
   * @param {string} productId
   * @returns {Promise<{review_summary: string, review_summary_generated_at: string}>}
   */
  generateReviewSummary: async (productId) => {
    return api.post(`/api/admin/products/${productId}/generate-review-summary`);
  },
};

const adminProductRelatedService = {
  /**
   * @param {string} productId
   * @returns {Promise<Array<Object>>}
   */
  getRelatedPicks: async (productId) => {
    const data = await api.get(`/api/admin/products/${productId}/related-picks`);
    assertArray(data, 'adminProductRelatedService.getRelatedPicks');
    return data;
  },

  /**
   * @param {string} productId
   * @param {string} relatedProductId
   */
  addRelatedPick: async (productId, relatedProductId) => {
    return api.post(`/api/admin/products/${productId}/related-picks`, { related_product_id: relatedProductId });
  },

  /**
   * @param {string} productId
   * @param {string[]} orderedIds
   */
  reorderRelatedPicks: async (productId, orderedIds) => {
    return api.put(`/api/admin/products/${productId}/related-picks/reorder`, { ordered_ids: orderedIds });
  },

  /**
   * @param {string} productId
   * @param {string} relatedProductId
   */
  deleteRelatedPick: async (productId, relatedProductId) => {
    return api.del(`/api/admin/products/${productId}/related-picks/${relatedProductId}`);
  },
};

const adminProductSimilarPinsService = {
  /**
   * @param {string} productId
   * @returns {Promise<Array<Object>>}
   */
  getSimilarPins: async (productId) => {
    const data = await api.get(`/api/admin/products/${productId}/similar-pins`);
    assertArray(data, 'adminProductSimilarPinsService.getSimilarPins');
    return data;
  },

  /**
   * @param {string} productId
   * @param {string} similarProductId
   */
  addSimilarPin: async (productId, similarProductId) => {
    return api.post(`/api/admin/products/${productId}/similar-pins`, { similar_product_id: similarProductId });
  },

  /**
   * @param {string} productId
   * @param {string[]} orderedIds
   */
  reorderSimilarPins: async (productId, orderedIds) => {
    return api.put(`/api/admin/products/${productId}/similar-pins/reorder`, { ordered_ids: orderedIds });
  },

  /**
   * @param {string} productId
   * @param {string} similarProductId
   */
  deleteSimilarPin: async (productId, similarProductId) => {
    return api.del(`/api/admin/products/${productId}/similar-pins/${similarProductId}`);
  },
};

const adminProductTrustPillarsService = {
  /**
   * @param {string} productId
   * @returns {Promise<Array<{ pillar_id: string, display_order: number }>>}
   */
  getTrustPillars: async (productId) => {
    const data = await api.get(`/api/admin/products/${productId}/trust-pillars`);
    assertArray(data, 'adminProductTrustPillarsService.getTrustPillars');
    return data;
  },

  /**
   * @param {string} productId
   * @param {string} pillarId
   */
  addTrustPillar: async (productId, pillarId) => {
    return api.post(`/api/admin/products/${productId}/trust-pillars`, { pillar_id: pillarId });
  },

  /**
   * @param {string} productId
   * @param {string[]} orderedIds
   */
  reorderTrustPillars: async (productId, orderedIds) => {
    return api.put(`/api/admin/products/${productId}/trust-pillars/reorder`, { ordered_ids: orderedIds });
  },

  /**
   * @param {string} productId
   * @param {string} pillarId
   */
  deleteTrustPillar: async (productId, pillarId) => {
    return api.del(`/api/admin/products/${productId}/trust-pillars/${pillarId}`);
  },
};

export {
  adminProductAiService,
  adminProductImageService,
  adminProductRelatedService,
  adminProductSimilarPinsService,
  adminProductTrustPillarsService,
  adminProductVariantService,
};
